import React from 'react';

import { Loader } from '@opendoor/atoms/loader';

import { AsyncFeatureFlagGate, type DefaultAsyncFeatureFlagProps } from '../flags/FeatureFlagGate';
import { ACCOUNT_FORM_FLAG } from '../flags/flagKeys';
import type { IAccountFormProps } from './accountFormProps';
import { AccountFormV2 } from './AccountFormV2/AccountFormV2';
import { adaptLegacyAccountFormProps } from './AccountFormV2/adaptLegacyAccountFormProps';

type AccountFormContainerProps = {
  accountFormProps: IAccountFormProps;
  featureFlagProps: DefaultAsyncFeatureFlagProps;
};

/**
 * Both arms render V2, and that is deliberate — not a gate left half-wired.
 *
 * `ACCOUNT_FORM_FLAG` is a long-lived experiment slot for the account form,
 * not a one-shot migration switch: control holds the shipped baseline, and
 * treatment carries whatever variant is under test. Between experiments the
 * two coincide, which is what makes winding the rollout up or down invisible
 * to users. A variant goes in the `enabled` arm; when it wins it moves to
 * `disabled` and the slot reopens.
 *
 * So don't collapse the gate on the grounds that both arms match.
 */
export const AccountForm: React.FC<AccountFormContainerProps> = ({
  accountFormProps,
  featureFlagProps,
}) => {
  const {
    flag = ACCOUNT_FORM_FLAG,
    loading = <Loader size="sm" />,
    ...flagProps
  } = featureFlagProps;
  const form = <AccountFormV2 {...adaptLegacyAccountFormProps(accountFormProps)} />;
  return (
    <AsyncFeatureFlagGate
      flag={flag}
      disabled={form}
      enabled={form}
      loading={loading}
      {...flagProps}
    />
  );
};
